/**
 * Datumsleutels in de LOKALE tijdzone van het kind.
 *
 * WAAROM DIT BESTAAT
 * ------------------
 * Overal in de app werd `new Date().toISOString().split('T')[0]` gebruikt om
 * "vandaag" te bepalen. Dat is de UTC-datum, niet de Nederlandse. In de
 * zomertijd (UTC+2) betekent dat: tussen 22:00 en middernacht is het volgens
 * die code al morgen.
 *
 * Voor een kind dat 's avonds oefent is dat direct zichtbaar en oneerlijk:
 *   - Om 22:15 oefenen zet `lastActiveDate` op de dag van morgen. De teller
 *     van vandaag springt op nul, dus het dagdoel dat al gehaald was, is
 *     ineens weer leeg.
 *   - De volgende ochtend is `today` (UTC) gelijk aan de opgeslagen datum, dus
 *     de middernacht-reset slaat over en de teller van gisteravond loopt door.
 *
 * Erger nog was de menging: `useAppStore` vergeleek een UTC-datumstring met
 * een LOKAAL geparste `new Date(lastActive)` om de weekgrens te bepalen. Twee
 * verschillende tijdrekeningen in één vergelijking geeft een uitkomst die
 * niemand kan narekenen — en een streak is precies iets wat een kind wél
 * naloopt.
 *
 * Een streak hoort te breken omdat er een dag niet geoefend is, nooit omdat de
 * server in Greenwich staat.
 *
 * LET OP: gebruik deze functies NIET voor exportbestandsnamen of
 * `<input type="date" min=...>`; daar is de bestaande ISO-vorm prima.
 */

/**
 * De lokale kalenderdatum als `YYYY-MM-DD`.
 *
 * Bewust met `getFullYear/getMonth/getDate` in plaats van een offsetberekening
 * op de timestamp: die tweede vorm gaat mis op de dagen dat de zomertijd
 * ingaat of afloopt, en dat zijn precies de dagen waarop niemand het nakijkt.
 */
export function localDateKey(date: Date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Parseert een `YYYY-MM-DD` uit `localDateKey` terug naar middernacht LOKALE
 * tijd.
 *
 * `new Date('2026-08-11')` doet dit NIET: die vorm wordt door de standaard als
 * UTC-middernacht gelezen, dus in Nederland komt er 01:00 of 02:00 uit. Bij het
 * vergelijken van twee dagen scheelt dat net genoeg om een dag over te slaan.
 *
 * Geeft `null` bij een onbruikbare waarde, zodat de aanroeper kan besluiten
 * niets te doen in plaats van met een `Invalid Date` te gaan rekenen — daar
 * komt `NaN` uit, en elke vergelijking daarmee is onwaar, wat er als "geen
 * nieuwe week" uitziet.
 */
export function parseLocalDateKey(key: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
  if (!match) return null;
  const [, year, month, day] = match;
  const parsed = new Date(Number(year), Number(month) - 1, Number(day));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** Hele kalenderdagen tussen twee lokale datums (b - a). */
export function daysBetweenLocal(a: Date, b: Date): number {
  const startA = new Date(a.getFullYear(), a.getMonth(), a.getDate()).getTime();
  const startB = new Date(b.getFullYear(), b.getMonth(), b.getDate()).getTime();
  return Math.round((startB - startA) / 86_400_000);
}